/**
 * Central in-flight-upload tracker + `beforeunload` guard.
 *
 * A tab close / reload mid-upload silently abandons the upload — the bytes never
 * finalize and the attachment is stranded (recovered only by the 48h orphan
 * sweep). While ≥1 upload is active ANYWHERE in the app, this registers a single
 * `beforeunload` listener so the browser shows its native "Leave site?" prompt.
 *
 * Every resumable upload routes through `performResumableUpload`, which calls
 * `beginUpload()` / `endUpload()` around each transfer — so the guard applies
 * everywhere without per-surface wiring. Counter (not boolean) so concurrent or
 * back-to-back uploads keep the guard up until the LAST one settles.
 */

let activeUploads = 0;

function warnBeforeUnload(e: BeforeUnloadEvent): void {
  // Triggers the browser's native confirm prompt. The text is browser-controlled
  // ("Leave site? Changes you made may not be saved"); `returnValue` must be set
  // (and preventDefault called) for the prompt to appear across browsers.
  e.preventDefault();
  e.returnValue = '';
}

export function beginUpload(): void {
  activeUploads += 1;
  if (activeUploads === 1 && typeof window !== 'undefined') {
    window.addEventListener('beforeunload', warnBeforeUnload);
  }
}

export function endUpload(): void {
  if (activeUploads === 0) return;
  activeUploads -= 1;
  if (activeUploads === 0 && typeof window !== 'undefined') {
    window.removeEventListener('beforeunload', warnBeforeUnload);
  }
}

/** @internal Test seam: number of uploads currently in flight. */
export function activeUploadCount(): number {
  return activeUploads;
}
